import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, Search, LayoutDashboard, Flame } from "lucide-react";
import Marquee from "react-fast-marquee";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();

  return (
    <div className="sticky top-0 z-50">
      <div className="bg-[#4A148C] text-[#FFCF52] text-xs sm:text-sm py-2">
        <Marquee gradient={false} speed={40} pauseOnHover>
          <span className="mx-8 tracking-wide font-body">
            <span className="diya-flame inline-block mr-2">✦</span>
            Diwali Grand Sale — Up to 50% off on handpicked handicrafts
            <span className="diya-flame inline-block mx-3">✦</span>
            Free shipping on orders above ₹2000
            <span className="diya-flame inline-block mx-3">✦</span>
            Handcrafted in India, delivered worldwide
          </span>
        </Marquee>
      </div>

      <header className="glass-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
          <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3">
            <div className="relative">
              <Flame className="w-7 h-7 text-[#E65C00] diya-flame" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-2xl font-semibold text-[#2C1810]">Mona&apos;s Creations</div>
              <div className="font-body text-[10px] tracking-[0.22em] uppercase text-[#4A148C]">
                Indian Handicrafts
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8 font-body text-sm text-[#2C1810]">
            <Link to="/" data-testid="nav-home" className="hover:text-[#E65C00] transition-colors">Home</Link>
            <Link to="/shop" data-testid="nav-shop" className="hover:text-[#E65C00] transition-colors">Shop</Link>
            <Link to="/shop?featured=1" data-testid="nav-featured" className="hover:text-[#E65C00] transition-colors">Festive Picks</Link>
            <Link to="/orders" data-testid="nav-orders" className="hover:text-[#E65C00] transition-colors">My Orders</Link>
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="header-search-btn"
              onClick={() => navigate("/shop")}
              className="w-10 h-10 rounded-full hover:bg-[#FFE9CE] flex items-center justify-center transition-colors"
              aria-label="Search"
            >
              <Search className="w-5 h-5 text-[#2C1810]" />
            </button>
            <Link to="/cart" data-testid="header-cart-link" className="relative w-10 h-10 rounded-full hover:bg-[#FFE9CE] flex items-center justify-center transition-colors">
              <ShoppingBag className="w-5 h-5 text-[#2C1810]" />
              {count > 0 && (
                <span data-testid="header-cart-count" className="absolute -top-1 -right-1 bg-[#E65C00] text-white text-[10px] font-semibold w-5 h-5 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-btn" className="w-10 h-10 rounded-full bg-[#4A148C] text-[#FFCF52] flex items-center justify-center hover:bg-[#6A2AB0] transition-colors">
                    <User className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-display">
                    Namaste, {user.name}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-orders" onClick={() => navigate("/orders")}>
                    <ShoppingBag className="w-4 h-4 mr-2" /> My Orders
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                data-testid="header-login-btn"
                onClick={() => navigate("/login")}
                className="rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white btn-lift px-5"
              >
                Login
              </Button>
            )}
          </div>
        </div>
      </header>
    </div>
  );
}
